"use client";

import { useEffect, useMemo, useState } from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  THEME_CHANGE_EVENT,
  getTheme,
  resolveTheme,
  setTheme,
  applyThemeClass,
  type ThemeMode,
  type ResolvedThemeMode,
} from "@/utils/theme";

function ThemeIcon({ resolvedTheme }: { resolvedTheme: ResolvedThemeMode }) {
  if (resolvedTheme === "dark") {
    return <Moon className="size-5" aria-hidden="true" />;
  }

  return <Sun className="size-5" aria-hidden="true" />;
}

export default function ThemeToggle() {
  const [theme, setThemeState] = useState<ThemeMode>("system");
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedThemeMode>("light");

  useEffect(() => {
    const syncTheme = () => {
      const activeTheme = getTheme();
      setThemeState(activeTheme);
      setResolvedTheme(resolveTheme(activeTheme));
    };

    syncTheme();
    window.addEventListener(THEME_CHANGE_EVENT, syncTheme);

    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, syncTheme);
    };
  }, []);

  useEffect(() => {
    if (theme !== "system") {
      return;
    }

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onMediaChange = () => {
      const activeTheme = getTheme();
      if (activeTheme === "system") {
        const resolved = resolveTheme(activeTheme);
        applyThemeClass(resolved);
        setResolvedTheme(resolved);
      }
    };

    media.addEventListener("change", onMediaChange);
    return () => {
      media.removeEventListener("change", onMediaChange);
    };
  }, [theme]);

  const currentLabel = useMemo(() => {
    if (theme === "system") {
      return "System";
    }

    return theme === "dark" ? "Dark" : "Light";
  }, [theme]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="btn-ghost h-10 px-3"
          aria-label={`Theme: ${currentLabel}`}
        >
          <ThemeIcon resolvedTheme={resolvedTheme} />
          <span className="hidden lg:inline">{currentLabel}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(value) => {
            const nextTheme = value as ThemeMode;
            setTheme(nextTheme);
            setThemeState(nextTheme);
            setResolvedTheme(resolveTheme(nextTheme));
          }}
        >
          <DropdownMenuRadioItem value="system">
            <Monitor className="size-4" />
            System
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="light">
            <Sun className="size-4" />
            Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon className="size-4" />
            Dark
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

